import _ from 'lodash'
import {
  allowedAH033AlphabeticChars,
  allowedAH033Chars,
  escapeSpecialChars,
} from 'common/src/vtj/nimenmuutos-allowed-characters'
/* eslint-disable no-control-regex */
const REGEX_UNUSUAL_WHITE_SPACE =
  /[\u2000-\u200d\u2028-\u202f\u205f-\u206f\u00a0]/g
const REGEX_MULTI_SPACE = /\u0020{2,}/g
const REGEX_LINEBREAK = /[\u000a-\u000d\u0085]/g
/* eslint-enable no-control-regex */
const STRING_LINE_BREAK = '\u000a'
const STRING_SPACE = '\u0020'
const STRING_EMPTY = ''
const RULES_RESET = {
  latin1SupplementLetters: false,
  latin1CommonSpecialSansXss: false,
  basicLatin: false,
  space: false,
  micro: false,
}
const RULES_TEXT = { numbers: true, allowMultispace: true, legalCode: true }
const RULES_TEXT_INPUT = { ...RULES_TEXT, allowMultispace: false, trim: true }

export const sanitizeName = (name) => {
  return sanitize(name)
    .replace(new RegExp(`[^${escapeSpecialChars(allowedAH033Chars)}]`, 'g'), '')
    .replace(
      new RegExp(
        `([${allowedAH033AlphabeticChars}])([${allowedAH033AlphabeticChars}]+)`,
        'g'
      ),
      (match, $1, $2) => $1 + $2.toLowerCase()
    )
    .replace(/--/g, '-')
}

export const sanitizeTextInput = (string) => {
  return sanitize(string, RULES_TEXT_INPUT)
}

export const sanitizeFilename = (string) => {
  return String(string)
    .replace(/ä/gi, 'a')
    .replace(/[öå]/gi, 'o')
    .replace(/[^\w\.]/g, '_') // eslint-disable-line no-useless-escape
}

// do not use on encoded urls. Does not allow '%' etc. Decode first.
export const sanitizeUrl = (string) => {
  return sanitize(string, {
    ...RULES_RESET,
    latin1SupplementLetters: true,
    letters: true,
    numbers: true,
    plusMinus: true,
    decimals: true,
    safeUrlCharacters: true,
  })
}

export const sanitizeRouterLocation = (location) => {
  /**
   * This code has been moved from the old Menu component to here.
   * The initial logic of the sanitization remains unclear, because there are
   * no unit tests for what harmful scenarios this is trying to protect against.
   * For this reason, I am unable to judge whether this code is actually relevant
   * or a technical debt. I suspect the latter, but since there is no real harm
   * in this now, I'll leave it here.
   * The origin of this is from 2020, commit 369e057fe7f. Good luck.
   */
  const { pathname, search, hash } = location // type Location from 'react-router-dom'
  const sanitizedPath = pathname ? sanitizeUrl(pathname) : ''
  const sanitizedSearch = search?.includes('?')
    ? `?${new URLSearchParams(search).toString()}`
    : ''
  const sanitizedHash = hash?.includes('#')
    ? `#${sanitizeUrl(hash.split('#')[1])}`
    : ''
  return `${sanitizedPath}${sanitizedSearch}${sanitizedHash}`
}

export const sanitize = (string, options = {}) => {
  const {
    lineBreaksToSpace = true,
    latin1SupplementLetters = true,
    latin1CommonSpecialSansXss = false,
    basicLatin = true,
    space = true,
    allowMultispace = false,
    trim = false,
    latin1Supplement = false,
    basicPunctuation = false,
    lineBreak = false,
    letters = false,
    numbers = false,
    decimals = false,
    plusMinus = false,
    transformToLowerCase = false,
    legalCode = false,
    micro = true,
    safeUrlCharacters = false,
  } = options

  return _.flow([
    getWhiteSpaceReplacer(),
    getLineBreakReplacer({ lineBreaksToSpace }),
    getNotAllowedReplacer({
      latin1SupplementLetters,
      latin1CommonSpecialSansXss,
      latin1Supplement,
      basicPunctuation,
      basicLatin,
      lineBreak,
      space,
      letters,
      numbers,
      decimals,
      plusMinus,
      legalCode,
      micro,
      safeUrlCharacters,
    }),
    getMultiSpaceReplacer({ allowMultispace }),
    getTrimmer({ trim }),
    getLowerCaseTransformer({ transformToLowerCase }),
  ])(String(string))
}

const getWhiteSpaceReplacer = () => {
  return (string) => string.replace(REGEX_UNUSUAL_WHITE_SPACE, STRING_SPACE)
}

const getLineBreakReplacer = ({ lineBreaksToSpace }) => {
  const replacement = lineBreaksToSpace ? STRING_SPACE : STRING_LINE_BREAK
  return (string) => string.replace(REGEX_LINEBREAK, replacement)
}

export const getDisallowedCharactersRegex = (options) =>
  new RegExp(
    '[^' +
      [
        options.latin1SupplementLetters
          ? '\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u00ff'
          : '',
        /**
         * Cherry picked common special characters, that do not include:
         * - common xss characters: <>"'%;()&+
         * - extra characters not accepted by VTJKysely: #=$
         */
        options.latin1CommonSpecialSansXss
          ? '\u0021\u002A\u002C-\u002F\u0040'
          : '',
        options.latin1Supplement ? '\u00a1-\u00ac\u00ae-\u00ff' : '',
        options.basicPunctuation
          ? '\u0021-\u002f\u003a-\u0040\u005b-\u005e\u0060\u007b-\u007e'
          : '',
        options.basicLatin ? '\u0021-\u007E' : '',
        options.lineBreak ? STRING_LINE_BREAK : '',
        options.space ? STRING_SPACE : '',
        options.letters ? 'A-Za-z' : '',
        options.numbers ? '0-9' : '',
        options.decimals ? ',.' : '',
        options.plusMinus ? '\\-\\+' : '',
        options.legalCode ? '\u00a7' : '',
        options.micro ? '\u00b5' : '', // A.K.A. the greek mu
        options.safeUrlCharacters ? '?=&#/_@;:' : '',
      ].join('') +
      ']',
    'g'
  )

const getNotAllowedReplacer = (options) => {
  const pattern = getDisallowedCharactersRegex(options)
  return (string) => string.replace(pattern, STRING_EMPTY)
}

const getMultiSpaceReplacer = ({ allowMultispace }) => {
  return (string) =>
    allowMultispace ? string : string.replace(REGEX_MULTI_SPACE, STRING_SPACE)
}

const getTrimmer = ({ trim }) => {
  return (string) => (trim ? string.trim() : string)
}

const getLowerCaseTransformer = ({ transformToLowerCase }) => {
  return (string) => (transformToLowerCase ? _.toLower(string) : string)
}
